import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IconCalendarEvent, IconSchool, IconStar } from "@tabler/icons-react";
import Skeleton from "../../../components/skeleton/Skeleton";
import { Button } from "../../../components/ui/Button";
import { DesktopChipRow } from "../../../components/ui/DesktopChipRow";
import {
  NewContentDot,
  TAB_BAR_LAYOUTS,
  TabButton,
} from "../../../components/ui/TabButton";
import { useNow } from "../../../common/hooks/useNow";
import { useActiveLang } from "../../../domains/app/hooks/useActiveLang";
import { resourceItemKey } from "../../../../../api/src/shared/domain/room-resources";
import {
  toResourceListItems,
  cardsOf,
  useGetRoomResourceItemsQuery,
  type RoomResourceListItem,
} from "../../../domains/room/hooks/useGetRoomResourceItemsQuery";
import type { ResolvedRoomSection } from "../../../domains/room/hooks/useGetRoomSectionsQuery";
import { resourceChips } from "../../../domains/room/utils/resourceChips";
import {
  roomResourceDate,
  roomResourceImageUrl,
  roomResourceSubject,
  roomResourceTarget,
  roomResourceTitle,
} from "../../../domains/room/utils/roomResourceItem";
import { usePlayAudioContent } from "../../../domains/room/hooks/usePlayAudioContent";
import { useLoadedAudioId } from "../../../domains/app/contexts/audio.context";
import {
  DesktopArticleRow,
  DesktopArticleRowSkeleton,
} from "../../../components/discovery/DesktopArticleRow";
import type { PrivateRoom } from "../../../domains/room/privateRoom";
import RoomEventDisplay from "../../../components/room/RoomEventDisplay";
import { EventDisplayDestination } from "../../../../../api/src/shared/domain/room";
import { isB2bOrganisation } from "../../../../../api/src/shared/domain/organisation";
import { DesktopRoomShell } from "./DesktopRoomShell";
import { RoomCongressResources } from "../../../components/room/RoomCongressResources";
import { DesktopCongressPdfViewer } from "../../../components/congress/DesktopCongressPdfViewer";
import { partnerAccentColor } from "../../../domains/congress/partnerAccentColor";
import { z } from "zod";
import { useLocationState } from "../../../domains/app/hooks/useLocationState";
import { useApiQuery } from "../../../services/api";
import { memoryStore } from "../../../lib/store";
import type { SupportedLanguage } from "../../../interfaces";
import {
  OtherRoomsSection,
  RoomTopicsSection,
  resourceTypeLabel,
} from "./DesktopRoomRail";

// One ticker for the tab: every congress chip reads the same minute.
const CONGRESS_TICK_MS = 60_000;

const CHIP_STYLE = TAB_BAR_LAYOUTS.desktop;

type ResourceChip = ReturnType<typeof resourceChips>[number];

/**
 * What the list shows for a `?section=`: the merged list on "All" (and on an
 * unknown id, a section since deleted), else that section's items, in CX's
 * order. Only the merged list shows more. Exported for its test.
 */
export function resourcesSelection(
  resources: {
    sections: readonly ResolvedRoomSection[];
    items: RoomResourceListItem[];
    canShowMore: boolean;
  },
  sectionParam: string | null
): {
  section: ResolvedRoomSection | null;
  items: RoomResourceListItem[];
  canShowMore: boolean;
} {
  const section =
    sectionParam === null
      ? undefined
      : resources.sections.find((s) => s.id === sectionParam);
  if (section === undefined)
    return {
      section: null,
      items: resources.items,
      canShowMore: resources.canShowMore,
    };
  return {
    section,
    items: toResourceListItems(section.orderedItems),
    canShowMore: false,
  };
}

const DOCUMENT_PARAM = "document";

/**
 * What a chip does (`null` is "All"): it filters the tab in place through
 * `?section=`. A congress chip shows its space in the tab (Figma
 * "D3.explore.room.resources.congress": the Congress Space is a section of the
 * room) and stamps "chip", the `entry_point` that `congress_space_viewed`
 * reports, an analytics contract. Exported for its test.
 */
export function chipTarget(
  chip: ResourceChip | null,
  search: string
): { search: string; state: { congressEntryPoint: "chip" } | null } {
  const next = new URLSearchParams(search);
  // An open document belongs to the space it came from: changing chips
  // closes it, else a return to that space would reopen it.
  next.delete(DOCUMENT_PARAM);
  if (chip === null) {
    next.delete("section");
    return { search: searchString(next), state: null };
  }
  next.set("section", chip.id);
  switch (chip.kind) {
    case "section":
      return { search: searchString(next), state: null };
    case "congress":
      return {
        search: searchString(next),
        state: { congressEntryPoint: "chip" },
      };
    default:
      throw new Error(chip satisfies never);
  }
}

function searchString(params: URLSearchParams): string {
  const query = params.toString();
  return query === "" ? "" : `?${query}`;
}

// The congress document read over the tab: the congress chip's PDFs open here,
// over the tab, so reading one leaves the reader where they were. The URL alone
// says which document is open (`?document=`): a reload, a shared link, Back and
// Forward all show what it names.
// Stamped on the entry the tab pushes when it opens a document: closing it
// then goes back to the entry it came from. A cold link to the document has
// no entry before it in the tab, so closing drops the parameter in place.
// A close in flight: a second click before the router moves (a double-click
// on the backdrop) must not go back once more. It ends with that move, so a
// viewer that Forward brings back closes again.
const documentClose = memoryStore<"idle" | "closing">("idle");
const documentLocationStateSchema = z.object({
  documentOpenedInTab: z.boolean().default(false),
});

/**
 * A room's Resources tab on a desktop (Figma "D3.explore.room.resources",
 * proposals 6b to 6d): what the partner shared, apart from Juisci's selection
 * and said so in a band at the partner's colour. A chip row above the list
 * narrows it to one section; its chips are the room's Resources order
 * (sections and congress spaces), as the server sent it.
 */
export function DesktopRoomResourcesPage({
  room,
}: {
  room: PrivateRoom;
}): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { activeLang, contentLang } = useActiveLang();
  const { documentOpenedInTab } = useLocationState(documentLocationStateSchema);
  const now = useNow(CONGRESS_TICK_MS);
  const resourcesQuery = useGetRoomResourceItemsQuery(room.id, "include");
  const isPartnerRoom = isB2bOrganisation(room.organisationModel.id);
  const sectionParam = new URLSearchParams(location.search).get("section");
  const documentParam = new URLSearchParams(location.search).get(
    DOCUMENT_PARAM
  );
  // The room's Resources order, rendered as is: never sorted here
  // (domain/room-order.ts). A congress space is a chip in that order, and has
  // no block of its own, else it would show twice.
  const chips =
    resourcesQuery.state === "success"
      ? resourceChips(resourcesQuery.entries, { now, contentLang, activeLang })
      : [];
  const selection =
    resourcesQuery.state === "success"
      ? resourcesSelection(
          {
            sections: resourcesQuery.sections,
            items: cardsOf(resourcesQuery.items),
            canShowMore: resourcesQuery.canShowMore,
          },
          sectionParam
        )
      : null;
  const selectedSection = selection === null ? null : selection.section;
  // A `?section=congress:<space>` shows that space in the tab, as the mobile
  // app's links already do.
  const selectedCongress = chips.find(
    (chip) => chip.kind === "congress" && chip.id === sectionParam
  );
  const congressSpaceId =
    selectedCongress === undefined || selectedCongress.kind !== "congress"
      ? null
      : selectedCongress.spaceId;
  const activeChipId =
    congressSpaceId !== null
      ? sectionParam
      : selectedSection === null
        ? null
        : selectedSection.id;
  // The section each item of "All" comes from, by its chip's label: the row
  // names it after the partner (Figma "bioMérieux · ESCMID 2026").
  const sectionLabels = new Map<string, string>();
  if (resourcesQuery.state === "success")
    for (const section of resourcesQuery.sections) {
      const chip = chips.find((c) => c.id === section.id);
      // A section with no name in any language adds nothing to the row.
      if (chip === undefined || chip.label === "") continue;
      for (const item of toResourceListItems(section.orderedItems)) {
        const key = resourceItemKey(item);
        if (!sectionLabels.has(key)) sectionLabels.set(key, chip.label);
      }
    }
  const items = selection === null ? [] : selection.items;
  const canShowMore = selection !== null && selection.canShowMore;
  function openChip(chip: ResourceChip | null) {
    const target = chipTarget(chip, location.search);
    history.replace({
      pathname: location.pathname,
      search: target.search,
      state: target.state,
    });
  }

  return (
    <DesktopRoomShell
      room={room}
      tab="resources"
      rail={
        <>
          <RoomTopicsSection room={room} selectedTopic={null} />
          <OtherRoomsSection room={room} />
        </>
      }
    >
      {/* Figma "Ready for dev": the partner's band first, then the chips,
          and no title over the list. */}
      {isPartnerRoom ? (
        <p className="m-0 mb-3 rounded-lg bg-ds-slate-100 px-3.5 py-2.5 text-footnote text-ds-slate-600">
          {t("Resources selected by {{partner}}", {
            partner: room.organisationModel.name,
          })}
        </p>
      ) : null}
      {resourcesQuery.state === "pending" ? (
        <ChipRowSkeleton />
      ) : chips.length === 0 ? null : (
        <DesktopChipRow>
          <TabButton
            className={CHIP_STYLE.chip}
            active={activeChipId === null}
            icon={
              <IconStar
                size={16}
                stroke={CHIP_STYLE.iconStroke}
                className="shrink-0"
              />
            }
            onClick={() => {
              openChip(null);
            }}
          >
            {t("All")}
          </TabButton>
          {chips.map((chip) => {
            const Icon =
              chip.icon === "congress" ? IconCalendarEvent : IconSchool;
            return (
              <TabButton
                key={chip.id}
                className={CHIP_STYLE.chip}
                active={chip.id === activeChipId}
                icon={
                  <>
                    {chip.newContent ? <NewContentDot /> : null}
                    <Icon
                      size={16}
                      stroke={CHIP_STYLE.iconStroke}
                      className="shrink-0"
                    />
                  </>
                }
                onClick={() => {
                  openChip(chip);
                }}
              >
                <span className="min-w-0 truncate">{chip.label}</span>
              </TabButton>
            );
          })}
        </DesktopChipRow>
      )}
      {congressSpaceId !== null ? (
        <div className="pt-8">
          <RoomCongressResources
            roomId={room.id}
            visible={true}
            scope={{ kind: "chip", spaceId: congressSpaceId }}
            now={now}
            layout={{
              kind: "desktop",
              onOpenDocument: (block) => {
                if (block.pdfUrl === null)
                  throw new Error(
                    "should not happen: a pdf document carries its pdfUrl"
                  );
                const next = new URLSearchParams(location.search);
                next.set(DOCUMENT_PARAM, block.id);
                history.push({
                  pathname: location.pathname,
                  search: searchString(next),
                  state: { documentOpenedInTab: true },
                });
              },
            }}
          />
        </div>
      ) : (
        <>
          <div className="border-b border-ds-slate-100 pt-3" />
          <RoomEventDisplay
            roomId={room.id}
            roomTextColor={null}
            pageType={EventDisplayDestination.RESOURCES}
          />
          {resourcesQuery.state === "pending" ? (
            Array.from({ length: 6 }, (_, index) => (
              <DesktopArticleRowSkeleton key={index} />
            ))
          ) : resourcesQuery.state === "error" ? (
            <p className="m-0 py-6 text-ui-body text-ds-slate-600">
              {t("Something went wrong. Please try again.")}
            </p>
          ) : items.length === 0 ? (
            <p className="m-0 py-6 text-ui-body text-ds-slate-600">
              {t("No resources available yet for this room")}
            </p>
          ) : (
            <>
              <div className="flex flex-col divide-y divide-ds-slate-100 border-b border-ds-slate-100">
                {items.map((item) => {
                  const key = resourceItemKey(item);
                  const section = sectionLabels.get(key);
                  return (
                    <ResourceRow
                      key={key}
                      room={room}
                      item={item}
                      partnerLabel={
                        !isPartnerRoom
                          ? null
                          : section === undefined || selectedSection !== null
                            ? room.organisationModel.name
                            : `${room.organisationModel.name} · ${section}`
                      }
                    />
                  );
                })}
              </div>
              {canShowMore ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-4 self-center"
                  onClick={resourcesQuery.showMore}
                >
                  {t("Show more")}
                </Button>
              ) : null}
            </>
          )}
        </>
      )}
      {congressSpaceId === null || documentParam === null ? null : (
        <CongressDocumentOverTab
          room={room}
          spaceId={congressSpaceId}
          documentId={documentParam}
          language={contentLang}
          onClose={() => {
            if (documentClose.get() === "closing") return;
            documentClose.set("closing");
            const release = history.listen(() => {
              release();
              documentClose.set("idle");
            });
            if (documentOpenedInTab) {
              history.goBack();
              return;
            }
            const next = new URLSearchParams(location.search);
            next.delete(DOCUMENT_PARAM);
            history.replace({
              pathname: location.pathname,
              search: searchString(next),
            });
          }}
        />
      )}
    </DesktopRoomShell>
  );
}

/**
 * The congress document that `?document=` names, read from the space's
 * content: the same query as the space's cards in the tab, so it is already
 * loaded when a card opens it. An id the space does not hold shows nothing.
 */
function CongressDocumentOverTab({
  room,
  spaceId,
  documentId,
  language,
  onClose,
}: {
  room: PrivateRoom;
  spaceId: string;
  documentId: string;
  language: SupportedLanguage;
  onClose: () => void;
}): React.JSX.Element | null {
  const query = useApiQuery("/webapp/private/congress-spaces/:id/content", {
    param: { id: spaceId },
    query: { language },
  });
  if (query.state !== "success") return null;
  const block = query.body.find(
    (candidate) => candidate.kind === "pdfCard" && candidate.id === documentId
  );
  if (block === undefined || block.kind !== "pdfCard" || block.pdfUrl === null)
    return null;
  return (
    <DesktopCongressPdfViewer
      label={block.labelType}
      title={block.documentTitle}
      organisationName={room.organisationModel.name}
      accentColor={partnerAccentColor(room)}
      pdfUrl={block.pdfUrl}
      onClose={onClose}
    />
  );
}

/**
 * A resource of the room, as the DS DesktopArticleRow Collection line (Figma
 * "Ready for dev"): the partner (and its section) above the title, the subject
 * under it, the type and date, the thumbnail. A resource the web cannot open
 * (a Nectar) is not listed.
 */
function ResourceRow({
  room,
  item,
  partnerLabel,
}: {
  room: PrivateRoom;
  item: RoomResourceListItem;
  /** `null` outside a partner room: Juisci's own resources name no one. */
  partnerLabel: string | null;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const history = useHistory();
  const { activeLang } = useActiveLang();
  const playAudioContent = usePlayAudioContent();
  const loadedAudioId = useLoadedAudioId();
  const target = roomResourceTarget(item, room.id);
  const open = (() => {
    switch (target.kind) {
      case "page":
        return () => {
          history.push(target.path);
        };
      case "audio":
        return () => {
          playAudioContent(target.content);
        };
      case "none":
        return null;
      default:
        throw new Error(target satisfies never);
    }
  })();
  if (open === null) return null;
  return (
    <DesktopArticleRow
      onRemove={null}
      layout="collection"
      title={roomResourceTitle(item)}
      source={
        partnerLabel === null ? null : { kind: "partner", label: partnerLabel }
      }
      author={roomResourceSubject(item)}
      description={null}
      date={roomResourceDate(item, activeLang)}
      readingMinutes={null}
      meta={resourceTypeLabel(item, t)}
      metaVariant="mint"
      access="closed"
      imageUrl={roomResourceImageUrl(item)}
      isInPlayer={item.kind === "content" && loadedAudioId === item.content.id}
      bookmark={null}
      onShare={null}
      onClick={open}
    />
  );
}

/** The chip row while the room's order loads: "All" and three sections. */
function ChipRowSkeleton(): React.JSX.Element {
  return (
    <div className="flex gap-2 py-1.25">
      {[56, 160, 112, 96].map((width) => (
        <Skeleton
          key={width}
          className="rounded-lg"
          width={width}
          height={32}
        />
      ))}
    </div>
  );
}
